import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { TaskDetail, TimelineEvent } from "../../api/types";
import { useCurrentUser } from "../../lib/auth";
import { Composer } from "../Composer";
import { ReviewDecision } from "../task/ReviewPanel";
import { ErrorBox, Spinner } from "../Spinner";
import { TimelineItem } from "./TimelineItem";

export function Timeline({
  task,
  events,
  isLoading,
  error,
}: {
  task: TaskDetail;
  events: TimelineEvent[] | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const waiting = task.status === "waiting_on_human";
  const working = task.status === "working";
  // Newest first; the API returns events oldest first.
  const newestFirst = useMemo(() => (events ? [...events].reverse() : []), [events]);

  return (
    <section className="timeline-section">
      <h3 className="section-label">Activity</h3>
      {isLoading && <Spinner />}
      {!!error && <ErrorBox error={error} />}
      <div className="timeline-composer">
        {task.status === "human_review" ? (
          <ReviewDecision task={task} timeline={events} />
        ) : (
          <Composer
            placeholder={waiting ? "Answer the agent's question…" : "Leave a comment…"}
            hint={
              waiting
                ? "The agent is waiting for your answer."
                : working
                  ? "Comments are delivered to the running agent."
                  : undefined
            }
            actions={[
              {
                label: waiting ? "Reply" : "Comment",
                variant: "primary",
                onSubmit: async (body, ids) => {
                  await api.comment(task.id, body, ids);
                  await qc.invalidateQueries({ queryKey: qk.timeline(task.id) });
                  qc.invalidateQueries({ queryKey: qk.task(task.id), exact: true });
                },
              },
            ]}
          />
        )}
      </div>
      <ol className="timeline">
        {newestFirst.map((ev) => (
          <TimelineItem key={ev.id} ev={ev} stages={task.stages} meId={me.id} pid={task.project_id} />
        ))}
      </ol>
    </section>
  );
}
